import {
  AnyAppEvent,
  Cache,
  createCacheHelpers,
  createGoogleAnalytics,
  EventTracker,
  Locale,
  TrackerCategory
} from '@sfstudios/shapeshifter';

export const gtag: Gtag.Gtag = function (..._args: unknown[]) {
  if (window.dataLayer == null) {
    return;
  }

  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);

  return;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

const polyfillGlobalThis = () => {
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(
    Object.assign(document.createElement('script'), {
      textContent:
        "window.globalThis = typeof globalThis === 'undefined' ? window : globalThis;"
    }),
    firstScript
  );
};

const initializeGoogleAnalytics = ({ id }: { id: string }) => {
  polyfillGlobalThis();
  const firstScript = document.getElementsByTagName('script')[0];
  const scriptTag = document.createElement('script');

  scriptTag.async = true;
  scriptTag.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  firstScript?.parentNode?.insertBefore(scriptTag, firstScript);

  if (window.dataLayer == null) {
    window.dataLayer = [];
  }

  gtag('js', new Date());
  gtag('config', id, { send_page_view: false, anonymize_ip: true });
};

const googleAnalyticsV4Tracker = ({
  gaId,
  cache,
  locale
}: {
  gaId: string;
  cache: Cache;
  locale: Locale;
}): EventTracker => {
  const cacheHelpers = createCacheHelpers(cache);
  const ga = createGoogleAnalytics({ locale, cacheHelpers });

  ga.setLogger(({ event, params }) => {
    gtag('event', event, params);
  });

  return {
    id: 'google-analytics-v4',
    category: TrackerCategory.Statistics,
    onRegister: () => {
      if (!gaId) return;
      initializeGoogleAnalytics({ id: gaId });

      ga.sendEvent('page_view', {
        pageTitle: document.title,
        pathname: window.location.pathname
      });
    },
    onEvent: (event: AnyAppEvent) => {
      switch (event.type) {
        case 'buyButton.clicked':
          ga.sendEvent('add_to_cart', event.payload);
          break;

        case 'playButton.clicked':
          ga.sendEvent(
            event.payload.isTrailer ? 'view_trailer' : 'video_start',
            { slug: event.payload.slug }
          );
          break;

        case 'search.inputBlurred':
          ga.sendEvent('view_search_results', event.payload);
          break;

        case 'list.itemClicked':
          ga.sendEvent('select_item', event.payload);
          break;

        case 'list.viewed':
          ga.sendEvent('view_item_list', event.payload);
          break;

        case 'checkout.transactionPaidForFirstTime':
          ga.sendEvent('purchase', event.payload);
          break;

        case 'account.created':
          ga.sendEvent('sign_up', event.payload);
          break;

        case 'session.loggedIn':
          ga.sendEvent('login', { type: event.payload.type });
          break;

        case 'notification.requested':
          ga.sendEvent('add_to_notification_list', event.payload);
          break;

        case 'favorites.added':
          ga.sendEvent('add_to_wishlist', event.payload);
          break;

        case 'cdp.viewed':
          ga.sendEvent('view_item', event.payload);
          break;

        case 'route.changed':
          ga.sendEvent('page_view', {
            pathname: event.payload.pathname,
            pageTitle: event.payload.pageTitle
          });
          break;

        default:
          break;
      }
    }
  };
};

export default googleAnalyticsV4Tracker;
